import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {SurfaceDragContext} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {HighlightedText} from '@mintdata-wsp/wsp/ui/components/text/HighlightedText';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./VisualComponentPreview.less');
const cx = classnames.bind(styles);

export enum VisualComponentPreviewMode
{
    IconBlock,
    ListItem,
}

export interface VisualComponentPreviewProps
{
    className?:string;
    component:VisualComponent;
    isComponentDragging?:boolean;
    mode:VisualComponentPreviewMode;
    highlightNamePart?:string;
}

@observer
export class VisualComponentPreview extends React.Component<VisualComponentPreviewProps, any>
{
    private _elem:HTMLElement;

    @bind
    _onRef(elem:HTMLElement)
    {
        this._elem = elem;
    }

    @action.bound
    _onMouseDown(e:React.MouseEvent<any>)
    {
        const {surfaceGlass} = controllers.ui;
        //if not right button is clicked...
        if( e.button !== 2 )
        {
            const browserEvent = e.nativeEvent;
            const boundingClientRect = this._elem.getBoundingClientRect();

            const ctx = new SurfaceDragContext(this._elem, boundingClientRect, browserEvent, this.props.component);

            surfaceGlass.setDragContext(ctx);

            if( e.metaKey )
                surfaceGlass.enableReparenting();
        }
    }

    _renderIconBlock()
    {
        const {className, component} = this.props;
        const isResolved = component.ref.isResolved;
        const controller = controllers.ui.componentIcons;
        const iconElement = controller.getIconForVisualComponent(component,
            WSPIconSize.xlarge_80,
            cx(
                {'resolvingVisualComponent': !isResolved},
                {'resolvedVisualComponent': isResolved}
            ));

        return <div ref={this._onRef} onMouseDown={this._onMouseDown}>
            <Column className={cx('previewArea', 'iconMode', className)}>
                {iconElement}
            </Column>
        </div>;
    }

    _renderDisplayName()
    {
        const {component, highlightNamePart} = this.props;
        const displayName = NameUtil.getDisplayName(component);

        return <HighlightedText text={displayName} partToHighlight={highlightNamePart} />;
    }

    @bind
    private _onClickIconDoc(e:React.MouseEvent<HTMLElement>, componentName:string):void
    {
        e.stopPropagation();
        e.preventDefault();

        const {lx} = controllers.ui;

        const dashedName = NameUtil.getDashedName(componentName);

        lx.showLxFrame();

        lx.postMessage({
            source: 'designer',
            event_type: 'show_component_docs',
            has_component_name: !!componentName,
            component_name: componentName ? dashedName : null
        });
    }

    private _renderListItem()
    {
        const {className, component, isComponentDragging} = this.props;
        const isResolved = component.ref.isResolved;
        const controller = controllers.ui.componentIcons;

        const iconElement = controller.getIconForVisualComponent(component,
            WSPIconSize.xlarge,
            cx(
                {'resolvingVisualComponent': !isResolved},
                {'resolvedVisualComponent': isResolved}
            ));

        return (
            <div
                ref={this._onRef}
                onMouseDown={this._onMouseDown}
                className={cx('componentWrapper')}
            >
                <Row className={cx('previewArea', 'listMode', {isDragging: isComponentDragging}, className)}>
                    <div className={cx('icon')}>
                        {iconElement}
                    </div>
                    <div className={cx('text')}>
                        {this._renderDisplayName()}
                    </div>
                    <div
                        className={cx('iconDoc')}
                        onMouseDown={(e:React.MouseEvent<HTMLElement>) => this._onClickIconDoc(e, component.name)}
                    >
                        <WSPIcon
                            name={WSPIcons.visualDesigner.spreadsheet.Help}
                            size={WSPIconSize.medium20}
                        />
                    </div>
                </Row>
            </div>
        );
    }

    render()
    {
        const {mode} = this.props;

        if( mode === VisualComponentPreviewMode.IconBlock )
            return this._renderIconBlock();
        else if( mode === VisualComponentPreviewMode.ListItem )
            return this._renderListItem();
    }
}
